import { useState, useCallback } from "react";
import { ArrowLeft, ArrowRight, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { saveMapping, validateImport } from "@/services/import";
import type {
  HeaderMatchResult,
  DetectHeadersResult,
  ValidationResult,
} from "@/types/import";

const SKIP_VALUE = "__skip__";

const TARGET_FIELDS = [
  { value: "external_key", label: "SKU / Artikelnummer", required: true },
  { value: "title", label: "Produktname", required: true },
  { value: "product_url", label: "Produkt-URL", required: true },
  { value: "image_url", label: "Bild-URL", required: true },
  { value: "price", label: "Preis", required: true },
  { value: "tags", label: "Tags / Schlagwörter", required: true },
  { value: "description", label: "Beschreibung", required: false },
  { value: "vendor", label: "Hersteller / Marke", required: false },
  { value: "compare_at_price", label: "Streichpreis (UVP)", required: false },
  { value: "inventory_hint", label: "Verfügbarkeit", required: false },
] as const;

const REQUIRED_FIELDS = TARGET_FIELDS.filter((f) => f.required).map(
  (f) => f.value,
);

interface MappingRow {
  csvHeader: string;
  targetField: string;
  confidence: HeaderMatchResult["confidence"];
}

interface MappingStepProps {
  jobId: string;
  headerResult: DetectHeadersResult;
  onBack: () => void;
  onDiscard: () => void;
  isDiscarding?: boolean;
  onSaveAndValidate: (result: ValidationResult) => void;
}

function buildInitialRows(headerResult: DetectHeadersResult): MappingRow[] {
  return headerResult.mappingSuggestions.map((s) => ({
    csvHeader: s.csvHeader,
    targetField: s.matchedField ?? SKIP_VALUE,
    confidence: s.confidence,
  }));
}

function ConfidenceBadge({
  confidence,
  isSkipped,
}: {
  confidence: HeaderMatchResult["confidence"];
  isSkipped: boolean;
}) {
  if (isSkipped) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        &#x2717;
      </span>
    );
  }
  if (confidence === "exact") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        &#x2713;
      </span>
    );
  }
  if (confidence === "alias" || confidence === "fuzzy") {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
        &#x2248;
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
      &#x2717;
    </span>
  );
}

export function MappingStep({
  jobId,
  headerResult,
  onBack,
  onDiscard,
  isDiscarding = false,
  onSaveAndValidate,
}: MappingStepProps) {
  const [rows, setRows] = useState<MappingRow[]>(() =>
    buildInitialRows(headerResult),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateHint, setDuplicateHint] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const busy = isSaving || isDiscarding;

  const mappedRequiredCount = REQUIRED_FIELDS.filter((rf) =>
    rows.some((r) => r.targetField === rf),
  ).length;

  const handleFieldChange = useCallback(
    (csvHeader: string, newTarget: string) => {
      setDuplicateHint(null);
      setRows((prev) => {
        const next = [...prev];

        // If another row already has this target field, reset it
        if (newTarget !== SKIP_VALUE) {
          const existingIdx = next.findIndex(
            (r) => r.targetField === newTarget && r.csvHeader !== csvHeader,
          );
          if (existingIdx !== -1) {
            const label =
              TARGET_FIELDS.find((f) => f.value === newTarget)?.label ??
              newTarget;
            setDuplicateHint(
              `"${label}" war bereits "${next[existingIdx]!.csvHeader}" zugeordnet und wurde zurückgesetzt.`,
            );
            next[existingIdx] = {
              ...next[existingIdx]!,
              targetField: SKIP_VALUE,
            };
          }
        }

        const rowIdx = next.findIndex((r) => r.csvHeader === csvHeader);
        if (rowIdx !== -1) {
          next[rowIdx] = { ...next[rowIdx]!, targetField: newTarget };
        }
        return next;
      });
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setError(null);
    setIsSaving(true);
    try {
      const mappings = rows
        .filter((r) => r.targetField !== SKIP_VALUE)
        .map((r) => ({ csvHeader: r.csvHeader, targetField: r.targetField }));

      await saveMapping(jobId, mappings);
      const validationResult = await validateImport(jobId);
      onSaveAndValidate(validationResult);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Beim Speichern ist ein Fehler aufgetreten.",
      );
    } finally {
      setIsSaving(false);
    }
  }, [rows, jobId, onSaveAndValidate]);

  const allRequiredMapped = mappedRequiredCount === REQUIRED_FIELDS.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Spalten zuordnen</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {mappedRequiredCount} von {REQUIRED_FIELDS.length} Pflichtfeldern
          zugeordnet
        </p>
      </div>

      {duplicateHint && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
          {duplicateHint}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">CSV-Spalte</th>
                <th className="px-4 py-3 text-left font-medium">Zuordnung</th>
                <th className="px-4 py-3 text-center font-medium">Status</th>
                <th className="px-4 py-3 text-center font-medium">Pflicht</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isRequired =
                  row.targetField !== SKIP_VALUE &&
                  REQUIRED_FIELDS.includes(
                    row.targetField as (typeof REQUIRED_FIELDS)[number],
                  );
                const isSkipped = row.targetField === SKIP_VALUE;

                return (
                  <tr key={row.csvHeader} className="border-b last:border-b-0">
                    <td className="px-4 py-3 font-mono text-xs">
                      {row.csvHeader}
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={row.targetField}
                        onValueChange={(val) =>
                          handleFieldChange(row.csvHeader, val)
                        }
                      >
                        <SelectTrigger className="h-9 w-56">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SKIP_VALUE}>
                            – Nicht importieren –
                          </SelectItem>
                          {TARGET_FIELDS.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                              {f.required ? " *" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <ConfidenceBadge
                        confidence={row.confidence}
                        isSkipped={isSkipped}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isRequired && (
                        <span className="text-amber-500">&#x2605;</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {confirmDiscard && (
        <p className="text-sm text-muted-foreground">
          Der hochgeladene Import und die Spalten-Zuordnung gehen verloren.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onBack} disabled={busy}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück
          </Button>
          {confirmDiscard ? (
            <Button variant="destructive" onClick={onDiscard} disabled={busy}>
              {isDiscarding ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Wirklich verwerfen?
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setConfirmDiscard(true)}
              disabled={busy}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Import verwerfen
            </Button>
          )}
        </div>
        <Button onClick={handleSave} disabled={!allRequiredMapped || busy}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ArrowRight className="mr-2 h-4 w-4" />
          )}
          Speichern &amp; Weiter
        </Button>
      </div>
    </div>
  );
}
